import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";
import PageHeader from "../../../components/PageHeader";
import { SELECT_STYLES } from "../../../components/DateFilters/constants";
import {
  BaseSemanalCelulaDia,
  BaseSemanalColunaDia,
  BaseSemanalItemMatriz,
  BaseSemanalResponse,
  controleProducaoService,
} from "../../../services/controleProducaoService";
import { lojaService } from "../../../services/lojaService";
import { Loja } from "../../../types/loja";
import { useAuth } from "../../../context/AuthContext";
import { canVerTodasLojasUser, hasFullAccessUser } from "../../../lib/screenAccess";
import {
  currentMondayISO,
  DIAS_SEMANA_COLUNAS_BASE,
  isoWeekdayTodaySP,
  isMondayISO,
  lastMondayOfMonthISO,
  mondayMinHistoricoISO,
  mondayOfISO,
} from "../../../utils/controleProducaoDates";
import { getDataFichaOperacional } from "../../../utils/controleProducaoTurno";
import { BrazilDate } from "../../../utils/BrazilDate";
import OrientacoesProducaoForm from "../../../components/ControleProducao/OrientacoesProducaoForm";
import UndMedidaSelect from "../../../components/ControleProducao/UndMedidaSelect";
import Modal from "../../../components/Modal";
import type {
  OrientacaoProducaoData,
  UndMedidaOption,
} from "../../../services/controleProducaoService";

type LinhasMatriz = Record<number, Record<number, string>>;

type LojaOpt = { value: number; label: string };

type HistoricoSemanaOpt = { value: string; label: string };

const LOJA_SELECT_STYLES = SELECT_STYLES as unknown as StylesConfig<
  LojaOpt,
  false
>;

const HISTORICO_SELECT_STYLES = SELECT_STYLES as unknown as StylesConfig<
  HistoricoSemanaOpt,
  false
>;

const AUTOSAVE_INTERVAL_MS = 20_000;
const hover3dCampoClass =
  "transition-all duration-200 ease-out hover:-translate-y-0.5 hover:drop-shadow-[0_10px_14px_rgba(15,23,42,0.18)] disabled:hover:translate-y-0 disabled:hover:drop-shadow-none";

function linhasVaziasPorDia(): Record<number, string> {
  const porDia: Record<number, string> = {};
  for (const d of DIAS_SEMANA_COLUNAS_BASE) {
    porDia[d.valor] = "";
  }
  return porDia;
}

function getCelulaDia(
  item: BaseSemanalItemMatriz,
  diaSemana: number
): BaseSemanalCelulaDia | undefined {
  const porDia = item.basesPorDia;
  if (porDia != null && typeof porDia === "object") {
    return porDia[diaSemana];
  }
  return undefined;
}

/** Garante `basesPorDia` mesmo se a API vier no formato antigo (um dia só). */
function normalizeItensMatriz(
  itens: BaseSemanalResponse["itens"],
  diaSemanaLegado?: number
): BaseSemanalItemMatriz[] {
  return itens.map((raw) => {
    const item = raw as BaseSemanalItemMatriz & {
      quantidadeBase?: string;
      sugestaoAnterior?: string;
      preenchidoDaSemanaAnterior?: boolean;
    };
    if (item.basesPorDia != null && typeof item.basesPorDia === "object") {
      const basesPorDia: Record<number, BaseSemanalCelulaDia> = {
        ...item.basesPorDia,
      };
      for (const d of DIAS_SEMANA_COLUNAS_BASE) {
        basesPorDia[d.valor] ??= {
          quantidadeBase: "",
          sugestaoAnterior: "",
          preenchidoDaSemanaAnterior: false,
        };
      }
      return { ...item, basesPorDia };
    }
    const diaRef = diaSemanaLegado ?? 1;
    const basesPorDia: Record<number, BaseSemanalCelulaDia> = {};
    for (const d of DIAS_SEMANA_COLUNAS_BASE) {
      basesPorDia[d.valor] = {
        quantidadeBase: d.valor === diaRef ? (item.quantidadeBase ?? "") : "",
        sugestaoAnterior: item.sugestaoAnterior ?? "",
        preenchidoDaSemanaAnterior:
          d.valor === diaRef
            ? (item.preenchidoDaSemanaAnterior ?? false)
            : false,
      };
    }
    return {
      itemId: item.itemId,
      descricao: item.descricao,
      pesoPadrao: item.pesoPadrao,
      undMedidaIdItem: item.undMedidaIdItem,
      undMedidaIdBaseItem: item.undMedidaIdBaseItem,
      undMedidaId: item.undMedidaId,
      setores: item.setores ?? [],
      basesPorDia,
    };
  });
}

type GrupoSetorBaseSemanal = {
  key: string;
  setorId: number | null;
  nome: string;
  itens: BaseSemanalItemMatriz[];
};

function setorPrimarioDoItem(item: BaseSemanalItemMatriz) {
  const setores = [...(item.setores ?? [])].sort(
    (a, b) =>
      (a.ordem ?? 0) - (b.ordem ?? 0) ||
      a.nome.localeCompare(b.nome, "pt-BR")
  );
  return setores[0] ?? null;
}

function nomesSetoresExtras(
  item: BaseSemanalItemMatriz,
  setorIdGrupo: number | null
): string {
  return (item.setores ?? [])
    .filter((s) => s.id !== setorIdGrupo)
    .map((s) => s.nome)
    .join(" · ");
}

function agruparItensPorSetor(
  itens: BaseSemanalItemMatriz[]
): GrupoSetorBaseSemanal[] {
  const temSetor = itens.some((item) => (item.setores?.length ?? 0) > 0);
  if (!temSetor) {
    return [{ key: "todos", setorId: null, nome: "", itens }];
  }

  const grupos = new Map<
    string,
    GrupoSetorBaseSemanal & { ordem: number }
  >();
  for (const item of itens) {
    const primario = setorPrimarioDoItem(item);
    const key = primario ? `setor-${primario.id}` : "sem-setor";
    if (!grupos.has(key)) {
      grupos.set(key, {
        key,
        setorId: primario?.id ?? null,
        nome: primario?.nome ?? "Sem setor",
        ordem: primario?.ordem ?? Number.MAX_SAFE_INTEGER,
        itens: [],
      });
    }
    grupos.get(key)!.itens.push(item);
  }

  return Array.from(grupos.values()).sort((a, b) => {
    if (a.setorId == null) return 1;
    if (b.setorId == null) return -1;
    return a.ordem - b.ordem || a.nome.localeCompare(b.nome, "pt-BR");
  });
}

function valorLinha(
  linhas: LinhasMatriz,
  itemId: number,
  diaSemana: number
): string {
  return linhas[itemId]?.[diaSemana] ?? "";
}

function buildLinhasUndFromItens(
  itens: BaseSemanalItemMatriz[]
): {
  linhas: LinhasMatriz;
  und: Record<number, number | "">;
} {
  const linhas: LinhasMatriz = {};
  const und: Record<number, number | ""> = {};
  for (const item of itens) {
    const porDia = linhasVaziasPorDia();
    for (const col of DIAS_SEMANA_COLUNAS_BASE) {
      porDia[col.valor] =
        getCelulaDia(item, col.valor)?.quantidadeBase ?? "";
    }
    linhas[item.itemId] = porDia;
    und[item.itemId] =
      item.undMedidaId ??
      item.undMedidaIdBaseItem ??
      item.undMedidaIdItem ??
      "";
  }
  return { linhas, und };
}

function sugestaoExibicao(
  itemId: number,
  diaSemana: number,
  cel: BaseSemanalCelulaDia | undefined,
  previewLinhas: LinhasMatriz | null,
  usarPreviewHistorico: boolean
): string {
  if (usarPreviewHistorico && previewLinhas) {
    const preview = previewLinhas[itemId]?.[diaSemana]?.trim();
    if (preview) {
      return preview;
    }
  }
  return cel?.sugestaoAnterior || "—";
}

function validarBaseColuna(
  itens: BaseSemanalItemMatriz[],
  linhas: LinhasMatriz,
  linhasUnd: Record<number, number | "">,
  diaSemana: number,
  labelDia: string
): string | null {
  if (itens.length === 0) {
    return "Cadastre itens de produção antes de salvar a base.";
  }
  for (const item of itens) {
    const und = linhasUnd[item.itemId];
    if (und === "" || und == null || und < 1) {
      return `Selecione a unidade de medida para "${item.descricao}".`;
    }
    if (valorLinha(linhas, item.itemId, diaSemana).trim() === "") {
      return `Preencha ${labelDia} para "${item.descricao}".`;
    }
  }
  return null;
}

const ControleProducaoBaseSemanalPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = hasFullAccessUser(user);
  const podeVerTodasLojas = canVerTodasLojasUser(user);
  const segundaAtual = currentMondayISO();
  const segundaMinima = mondayMinHistoricoISO();
  const segundaPadrao = useMemo(() => {
    const op = mondayOfISO(getDataFichaOperacional());
    return op > segundaAtual ? op : segundaAtual;
  }, [segundaAtual]);
  const segundaMaxima = useMemo(() => {
    const doMes = lastMondayOfMonthISO();
    const op = mondayOfISO(getDataFichaOperacional());
    return [doMes, op, segundaAtual].reduce((a, b) => (a > b ? a : b));
  }, [segundaAtual]);
  const [semana, setSemana] = useState(segundaPadrao);
  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    user?.lojaId
  );
  const [replicarParaLojaId, setReplicarParaLojaId] = useState<
    number | undefined
  >(undefined);
  const [historicoSemanas, setHistoricoSemanas] = useState<
    { dataSegunda: string; label: string }[]
  >([]);
  const [historicoOrigem, setHistoricoOrigem] = useState("");
  const [replicandoHistorico, setReplicandoHistorico] = useState(false);
  const [modalReplicarHistorico, setModalReplicarHistorico] = useState(false);
  const [previewHistoricoLinhas, setPreviewHistoricoLinhas] =
    useState<LinhasMatriz | null>(null);
  const [previewHistoricoUnd, setPreviewHistoricoUnd] = useState<
    Record<number, number | "">
  >({});
  const [loadingPreviewHistorico, setLoadingPreviewHistorico] = useState(false);
  const [undSobrescritosNaPreview, setUndSobrescritosNaPreview] = useState<
    Set<number>
  >(() => new Set());
  const [lojas, setLojas] = useState<Loja[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const skipNextAutosaveRef = useRef(false);
  const undAutosaveRef = useRef(false);
  const saveChainRef = useRef(Promise.resolve());
  const linhasAlteradasPeloUsuarioRef = useRef(false);
  const colunasPendentesRef = useRef<Set<number>>(new Set());
  const [colunaAutosaveAlvo, setColunaAutosaveAlvo] = useState<number | null>(
    null
  );
  const [payload, setPayload] = useState<BaseSemanalResponse | null>(null);
  const [linhas, setLinhas] = useState<LinhasMatriz>({});
  const [linhasUnd, setLinhasUnd] = useState<Record<number, number | "">>({});
  const [unds, setUnds] = useState<UndMedidaOption[]>([]);

  const lojaOptions = useMemo(
    () =>
      podeVerTodasLojas
        ? lojas
        : lojas.filter((loja) => loja.id === user?.lojaId),
    [podeVerTodasLojas, lojas, user?.lojaId]
  );

  const lojaSelectOptions = useMemo<LojaOpt[]>(
    () => lojaOptions.map((loja) => ({ value: loja.id, label: loja.nome })),
    [lojaOptions]
  );

  const selectedLojaOption =
    lojaSelectOptions.find((o) => o.value === selectedLojaId) ?? null;

  const replicarLojaSelectOptions = useMemo(
    () => lojaSelectOptions.filter((o) => o.value !== selectedLojaId),
    [lojaSelectOptions, selectedLojaId]
  );

  const replicarParaLojaOption =
    replicarLojaSelectOptions.find((o) => o.value === replicarParaLojaId) ??
    null;

  const historicoSelectOptions = useMemo<HistoricoSemanaOpt[]>(
    () =>
      historicoSemanas.map((s) => ({
        value: s.dataSegunda,
        label: s.label,
      })),
    [historicoSemanas]
  );

  const historicoOrigemOption =
    historicoSelectOptions.find((o) => o.value === historicoOrigem) ?? null;

  const diasColunas: BaseSemanalColunaDia[] = useMemo(() => {
    if (payload?.diasColunas?.length) {
      return payload.diasColunas;
    }
    return DIAS_SEMANA_COLUNAS_BASE.map((d) => ({
      diaSemana: d.valor,
      label: d.labelCurto,
      labelCompleto: d.label,
      ehHoje: isoWeekdayTodaySP() === d.valor,
      permiteEdicao: false,
    }));
  }, [payload?.diasColunas]);

  const itensMatriz = useMemo(
    () => normalizeItensMatriz(payload?.itens ?? []),
    [payload?.itens]
  );

  const gruposPorSetor = useMemo(
    () => agruparItensPorSetor(itensMatriz),
    [itensMatriz]
  );

  const colunasTabela = 3 + diasColunas.length;

  const loadBase = useCallback(async (dateValue: string, lojaId?: number) => {
    linhasAlteradasPeloUsuarioRef.current = false;
    undAutosaveRef.current = false;
    colunasPendentesRef.current.clear();
    setColunaAutosaveAlvo(null);
    setLoading(true);
    try {
      const monday = mondayOfISO(dateValue);
      const data = await controleProducaoService.getBaseSemanal(monday, lojaId);
      const diaLegado =
        typeof (data as { diaSemana?: number }).diaSemana === "number"
          ? (data as { diaSemana: number }).diaSemana
          : undefined;
      const itensNorm = normalizeItensMatriz(data.itens, diaLegado);
      setPayload({ ...data, matriz: true, itens: itensNorm });
      setUnds(data.unds ?? []);

      const { linhas: next, und: nextUnd } = buildLinhasUndFromItens(itensNorm);
      skipNextAutosaveRef.current = true;
      setLinhas(next);
      setLinhasUnd(nextUnd);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar base semanal.");
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadBase(semana, selectedLojaId);
  }, [semana, selectedLojaId, loadBase]);

  const loadHistorico = useCallback(async (dateValue: string, lojaId?: number) => {
    if (!isAdmin) {
      setHistoricoSemanas([]);
      setHistoricoOrigem("");
      setPreviewHistoricoLinhas(null);
      setPreviewHistoricoUnd({});
      return;
    }
    const loja = lojaId ?? undefined;
    if (!loja || loja < 1) {
      setHistoricoSemanas([]);
      setHistoricoOrigem("");
      setPreviewHistoricoLinhas(null);
      setPreviewHistoricoUnd({});
      return;
    }
    try {
      const data = await controleProducaoService.getHistoricoBaseSemanal(
        mondayOfISO(dateValue),
        loja
      );
      setHistoricoSemanas(data.semanas);
      setHistoricoOrigem("");
      setPreviewHistoricoLinhas(null);
      setPreviewHistoricoUnd({});
    } catch {
      setHistoricoSemanas([]);
      setHistoricoOrigem("");
      setPreviewHistoricoLinhas(null);
      setPreviewHistoricoUnd({});
    }
  }, [isAdmin]);

  useEffect(() => {
    void loadHistorico(semana, selectedLojaId);
  }, [semana, selectedLojaId, loadHistorico]);

  useEffect(() => {
    if (!historicoOrigem || !selectedLojaId || selectedLojaId < 1) {
      setPreviewHistoricoLinhas(null);
      setPreviewHistoricoUnd({});
      setUndSobrescritosNaPreview(new Set());
      return;
    }

    setUndSobrescritosNaPreview(new Set());
    let cancelled = false;
    setLoadingPreviewHistorico(true);
    void controleProducaoService
      .getBaseSemanal(historicoOrigem, selectedLojaId)
      .then((data) => {
        if (cancelled) return;
        const itensNorm = normalizeItensMatriz(data.itens);
        const preview = buildLinhasUndFromItens(itensNorm);
        setPreviewHistoricoLinhas(preview.linhas);
        setPreviewHistoricoUnd(preview.und);
      })
      .catch(() => {
        if (cancelled) return;
        setPreviewHistoricoLinhas(null);
        setPreviewHistoricoUnd({});
        toast.error("Erro ao carregar pré-visualização da semana selecionada.");
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingPreviewHistorico(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [historicoOrigem, selectedLojaId]);

  const handleSegundaChange = (raw: string) => {
    if (!raw) {
      setSemana(segundaAtual);
      return;
    }
    const monday = mondayOfISO(raw);
    if (!isMondayISO(raw)) {
      toast.info("Ajustado para a segunda-feira da semana selecionada.");
    }
    if (monday > segundaMaxima) {
      toast.warning(
        "Não é possível selecionar semanas após a última segunda-feira do mês."
      );
      return;
    }
    if (monday < segundaMinima) {
      toast.warning("Data fora do período de histórico disponível.");
      return;
    }
    setSemana(monday);
  };

  useEffect(() => {
    void lojaService
      .getLojas()
      .then(setLojas)
      .catch(() => setLojas([]));
  }, []);

  useEffect(() => {
    if (lojaOptions.length === 0) return;
    const valid = selectedLojaId != null && selectedLojaId >= 1;
    const exists = valid && lojaOptions.some((l) => l.id === selectedLojaId);
    if (!exists) {
      setSelectedLojaId(lojaOptions[0]?.id ?? user?.lojaId);
    }
  }, [lojaOptions, selectedLojaId, user?.lojaId]);

  const baseCompleta =
    diasColunas.length > 0 &&
    diasColunas.every((c) => c.colunaCompleta === true);
  const semanaCorrente =
    payload?.semanaCorrente ??
    (semana >= segundaAtual && semana <= segundaMaxima);
  const podeEditarOrientacao =
    isAdmin && (payload?.permiteEditarOrientacao ?? true);

  const colunaEditavelHoje = useMemo(
    () => diasColunas.find((c) => c.permiteEdicao) ?? null,
    [diasColunas]
  );

  const podeEditarAlgumaColuna =
    isAdmin && semanaCorrente && diasColunas.some((c) => c.permiteEdicao);

  const temSugestaoAnterior = useMemo(
    () =>
      itensMatriz.some((item) =>
        diasColunas.some(
          (col) =>
            getCelulaDia(item, col.diaSemana)?.preenchidoDaSemanaAnterior ===
            true
        )
      ),
    [itensMatriz, diasColunas]
  );

  const historicoOrigemLabel = useMemo(
    () =>
      historicoSemanas.find((s) => s.dataSegunda === historicoOrigem)?.label ??
      "",
    [historicoSemanas, historicoOrigem]
  );

  const usarPreviewHistorico = Boolean(
    historicoOrigem && previewHistoricoLinhas && !loadingPreviewHistorico
  );

  const formattedSegunda = useMemo(() => {
    if (!payload?.dataSegunda) return "";
    return BrazilDate.formatBR(
      BrazilDate.fromISOString(payload.dataSegunda),
      false
    );
  }, [payload?.dataSegunda]);

  const resolveLojaId = useCallback((): number | undefined => {
    if (selectedLojaId != null && selectedLojaId >= 1) {
      return selectedLojaId;
    }
    if (user?.lojaId != null && user.lojaId >= 1) {
      return user.lojaId;
    }
    return undefined;
  }, [selectedLojaId, user?.lojaId]);

  const montarLinhasSave = useCallback(
    (diaSemana: number) =>
      itensMatriz.map((item) => {
        const qty = valorLinha(linhas, item.itemId, diaSemana);
        const undMedidaId = linhasUnd[item.itemId];
        return {
          itemId: item.itemId,
          quantidadeBase: qty,
          ...(undMedidaId !== "" && undMedidaId >= 1 ? { undMedidaId } : {}),
        };
      }),
    [itensMatriz, linhas, linhasUnd]
  );

  const runSaveSerializado = useCallback(
    <T,>(task: () => Promise<T>): Promise<T> => {
      const next = saveChainRef.current.then(task, task);
      saveChainRef.current = next.then(
        () => undefined,
        () => undefined
      );
      return next;
    },
    []
  );

  const saveDia = useCallback(
    async (
      diaSemana: number,
      opts?: { silent?: boolean; suppressLoading?: boolean }
    ): Promise<{ ok: boolean; baseConfirmada?: boolean }> =>
      runSaveSerializado(async () => {
        if (!payload || !isAdmin) return { ok: false };
        const col = diasColunas.find((c) => c.diaSemana === diaSemana);
        if (!col?.permiteEdicao) {
          if (opts?.silent !== true) {
            toast.warning(
              `A base de ${col?.labelCompleto ?? "este dia"} não pode ser alterada agora.`
            );
          }
          return { ok: false };
        }
        const lojaId = resolveLojaId();
        if (!lojaId) {
          if (opts?.silent !== true) {
            toast.warning("Selecione uma loja válida antes de salvar.");
          }
          return { ok: false };
        }
        const silent = opts?.silent === true;
        const suppressLoading = opts?.suppressLoading === true;
        if (!suppressLoading) {
          if (silent) {
            setAutoSaving(true);
          } else {
            setSaving(true);
          }
        }
        try {
          const res = await controleProducaoService.saveBaseSemanal({
            dataSegunda: semana,
            diaSemana,
            lojaId,
            replicarParaLojaId:
              replicarParaLojaId != null && replicarParaLojaId >= 1
                ? replicarParaLojaId
                : undefined,
            linhas: montarLinhasSave(diaSemana),
          });
          if (res.baseConfirmada != null) {
            setPayload((prev) =>
              prev
                ? { ...prev, baseConfirmada: res.baseConfirmada === true }
                : prev
            );
          }
          colunasPendentesRef.current.delete(diaSemana);
          linhasAlteradasPeloUsuarioRef.current =
            colunasPendentesRef.current.size > 0;
          if (!silent) {
            const label =
              diasColunas.find((c) => c.diaSemana === diaSemana)?.label ??
              "dia";
            toast.success(`Base de ${label} salva.`);
            await loadBase(semana, selectedLojaId);
          }
          return { ok: true, baseConfirmada: res.baseConfirmada };
        } catch (error: any) {
          toast.error(error?.message || "Erro ao salvar base semanal.");
          return { ok: false };
        } finally {
          if (!suppressLoading) {
            if (silent) {
              setAutoSaving(false);
            } else {
              setSaving(false);
            }
          }
        }
      }),
    [
      runSaveSerializado,
      payload,
      isAdmin,
      diasColunas,
      semana,
      selectedLojaId,
      replicarParaLojaId,
      montarLinhasSave,
      loadBase,
      resolveLojaId,
    ]
  );

  useEffect(() => {
    if (loading || !payload?.itens.length || !isAdmin) return;
    if (!linhasAlteradasPeloUsuarioRef.current) return;
    if (skipNextAutosaveRef.current) {
      skipNextAutosaveRef.current = false;
      return;
    }

    const diasEditaveis = diasColunas
      .filter((c) => c.permiteEdicao)
      .map((c) => c.diaSemana);
    if (diasEditaveis.length === 0) return;

    const timer = window.setTimeout(() => {
      void (async () => {
        if (undAutosaveRef.current) {
          undAutosaveRef.current = false;
          for (const dia of diasEditaveis) {
            const ok = await saveDia(dia, {
              silent: true,
              suppressLoading: true,
            });
            if (!ok) return;
          }
          return;
        }

        const alvo =
          colunaAutosaveAlvo ??
          Array.from(colunasPendentesRef.current)[0] ??
          diasEditaveis[0];
        if (alvo == null) return;
        const col = diasColunas.find((c) => c.diaSemana === alvo);
        if (!col?.permiteEdicao) return;
        await saveDia(alvo, { silent: true, suppressLoading: true });
      })();
    }, AUTOSAVE_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [
    linhas,
    linhasUnd,
    loading,
    payload?.itens.length,
    isAdmin,
    colunaAutosaveAlvo,
    diasColunas,
    saveDia,
  ]);

  const marcarColunaAlterada = (diaSemana: number) => {
    colunasPendentesRef.current.add(diaSemana);
    linhasAlteradasPeloUsuarioRef.current = true;
    setColunaAutosaveAlvo(diaSemana);
  };

  const atualizarCelula = (
    itemId: number,
    diaSemana: number,
    valor: string
  ) => {
    marcarColunaAlterada(diaSemana);
    setLinhas((prev) => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] ?? linhasVaziasPorDia()),
        [diaSemana]: valor,
      },
    }));
  };

  const atualizarUnd = (itemId: number, undMedidaId: number | "") => {
    if (historicoOrigem) {
      setUndSobrescritosNaPreview((prev) => new Set(prev).add(itemId));
    }
    setLinhasUnd((prev) => ({ ...prev, [itemId]: undMedidaId }));
    undAutosaveRef.current = true;
    linhasAlteradasPeloUsuarioRef.current = true;
    for (const col of diasColunas.filter((c) => c.permiteEdicao)) {
      colunasPendentesRef.current.add(col.diaSemana);
    }
    const primeiroEditavel = diasColunas.find(
      (c) => c.permiteEdicao
    )?.diaSemana;
    if (primeiroEditavel != null) {
      setColunaAutosaveAlvo(primeiroEditavel);
    }
  };

  const replicarHistorico = useCallback(async () => {
    if (!historicoOrigem) {
      toast.warning("Selecione uma semana anterior para replicar.");
      return;
    }
    const lojaId = resolveLojaId();
    if (!lojaId) {
      toast.warning("Selecione uma loja válida.");
      return;
    }
    if (!semanaCorrente || !podeEditarAlgumaColuna) {
      toast.warning("A replicação só está disponível na semana corrente.");
      return;
    }
    setModalReplicarHistorico(true);
  }, [
    historicoOrigem,
    resolveLojaId,
    semanaCorrente,
    podeEditarAlgumaColuna,
  ]);

  const aplicarPreviewHistoricoNasLinhas = useCallback((): boolean => {
    if (!previewHistoricoLinhas) {
      return false;
    }
    const diasAplicar = diasColunas.map((c) => c.diaSemana);
    setLinhas((prev) => {
      const next: LinhasMatriz = { ...prev };
      for (const item of itensMatriz) {
        const porDia = { ...(next[item.itemId] ?? linhasVaziasPorDia()) };
        for (const dia of diasAplicar) {
          const qty = previewHistoricoLinhas[item.itemId]?.[dia]?.trim() ?? "";
          if (qty !== "") {
            porDia[dia] = previewHistoricoLinhas[item.itemId][dia];
          }
        }
        next[item.itemId] = porDia;
      }
      return next;
    });
    setLinhasUnd((prev) => {
      const next = { ...prev };
      for (const item of itensMatriz) {
        const und = previewHistoricoUnd[item.itemId];
        if (und !== "" && und != null && und >= 1) {
          next[item.itemId] = und;
        }
      }
      return next;
    });
    skipNextAutosaveRef.current = true;
    linhasAlteradasPeloUsuarioRef.current = false;
    colunasPendentesRef.current.clear();
    setColunaAutosaveAlvo(null);
    setHistoricoOrigem("");
    setPreviewHistoricoLinhas(null);
    setPreviewHistoricoUnd({});
    setUndSobrescritosNaPreview(new Set());
    return true;
  }, [
    previewHistoricoLinhas,
    previewHistoricoUnd,
    diasColunas,
    itensMatriz,
  ]);

  const apenasReplicarHistorico = useCallback(() => {
    if (!previewHistoricoLinhas) {
      toast.warning("Aguarde o carregamento da semana selecionada.");
      return;
    }
    if (!aplicarPreviewHistoricoNasLinhas()) {
      toast.warning("Não foi possível replicar os valores na tela.");
      return;
    }
    setModalReplicarHistorico(false);
    toast.info(
      "Valores replicados na tela. Revise e clique em Salvar base para gravar."
    );
  }, [previewHistoricoLinhas, aplicarPreviewHistoricoNasLinhas]);

  const replicarESalvarHistorico = useCallback(async () => {
    if (!historicoOrigem) return;
    const lojaId = resolveLojaId();
    if (!lojaId) return;

    setReplicandoHistorico(true);
    try {
      await controleProducaoService.replicarBaseSemanalHistorico({
        dataSegundaDestino: semana,
        dataSegundaOrigem: historicoOrigem,
        lojaId,
      });
      setModalReplicarHistorico(false);
      setHistoricoOrigem("");
      setPreviewHistoricoLinhas(null);
      setPreviewHistoricoUnd({});
      setUndSobrescritosNaPreview(new Set());
      toast.success("Base replicada e salva com sucesso.");
      await loadBase(semana, selectedLojaId);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao replicar base semanal.");
    } finally {
      setReplicandoHistorico(false);
    }
  }, [
    historicoOrigem,
    resolveLojaId,
    semana,
    selectedLojaId,
    loadBase,
  ]);

  const saveTudo = useCallback(async () => {
    const diasEditaveis = diasColunas.filter((c) => c.permiteEdicao);
    if (diasEditaveis.length === 0) {
      toast.warning("Nenhuma coluna disponível para salvar no momento.");
      return false;
    }
    for (const col of diasEditaveis) {
      const erro = validarBaseColuna(
        itensMatriz,
        linhas,
        linhasUnd,
        col.diaSemana,
        col.label
      );
      if (erro) {
        toast.warning(erro);
        return false;
      }
    }
    setSaving(true);
    try {
      let colunasSalvas = 0;
      for (const col of diasEditaveis) {
        const result = await saveDia(col.diaSemana, {
          silent: true,
          suppressLoading: true,
        });
        if (!result.ok) {
          return false;
        }
        colunasSalvas += 1;
      }
      colunasPendentesRef.current.clear();
      linhasAlteradasPeloUsuarioRef.current = false;
      setColunaAutosaveAlvo(null);
      toast.success(
        colunasSalvas === 1
          ? `Coluna ${diasEditaveis[0]?.label ?? ""} salva.`
          : `${colunasSalvas} colunas salvas.`
      );
      await loadBase(semana, selectedLojaId);
      return true;
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar base semanal.");
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    itensMatriz,
    linhas,
    linhasUnd,
    saveDia,
    loadBase,
    semana,
    selectedLojaId,
    diasColunas,
  ]);

  return (
    <main className="mx-auto max-w-[96rem] px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-4 mt-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <PageHeader
          title="Base Semanal de Produção"
          description="Uma coluna por dia de funcionamento. Produção libera quando a base do dia da ficha estiver salva e no turno da noite só sobra."
        />
        <div className="flex shrink-0 flex-wrap items-center gap-3">
          <ReactSelect<LojaOpt, false>
            inputId="base-semanal-loja-select"
            value={selectedLojaOption}
            onChange={(option: SingleValue<LojaOpt>) =>
              setSelectedLojaId(option?.value)
            }
            options={lojaSelectOptions}
            isClearable={isAdmin}
            placeholder="Selecionar loja"
            className="w-full min-w-[220px] sm:w-60"
            styles={LOJA_SELECT_STYLES}
          />
          <input
            type="date"
            value={semana}
            min={segundaMinima}
            max={segundaMaxima}
            step={7}
            onChange={(e) => handleSegundaChange(e.target.value)}
            aria-label="Segunda-feira da semana"
            title="Selecione uma segunda-feira do mês"
            className="h-[38px] w-full min-w-[10.5rem] rounded-md border border-neutral-300 px-3 text-sm sm:w-auto"
          />
          {isAdmin && (
            <ReactSelect<LojaOpt, false>
              inputId="base-semanal-replicar-loja-select"
              value={replicarParaLojaOption}
              onChange={(option: SingleValue<LojaOpt>) =>
                setReplicarParaLojaId(option?.value)
              }
              options={replicarLojaSelectOptions}
              isClearable
              placeholder="Replicar para loja"
              isDisabled={!podeEditarAlgumaColuna}
              className="w-full min-w-[220px] sm:w-60"
              styles={LOJA_SELECT_STYLES}
            />
          )}
        </div>
      </div>

      {isAdmin && semanaCorrente && podeEditarAlgumaColuna && (
        <div className="mb-4 flex flex-col gap-3 rounded-md border border-neutral-200 bg-neutral-50 px-4 py-3 sm:flex-row sm:items-center">
          <ReactSelect<HistoricoSemanaOpt, false>
            inputId="base-semanal-historico-select"
            value={historicoOrigemOption}
            onChange={(option: SingleValue<HistoricoSemanaOpt>) =>
              setHistoricoOrigem(option?.value ?? "")
            }
            options={historicoSelectOptions}
            isClearable
            placeholder="Semana passada (padrão)"
            isDisabled={
              replicandoHistorico ||
              saving ||
              loadingPreviewHistorico ||
              historicoSemanas.length === 0
            }
            noOptionsMessage={() => "Nenhuma semana anterior registrada"}
            className="w-full min-w-[280px] flex-1"
            styles={HISTORICO_SELECT_STYLES}
          />
          <button
            type="button"
            onClick={() => void replicarHistorico()}
            disabled={
              replicandoHistorico ||
              saving ||
              loadingPreviewHistorico ||
              !historicoOrigem ||
              historicoSemanas.length === 0
            }
            className="h-[38px] shrink-0 rounded-md border border-neutral-300 bg-white px-4 text-sm font-semibold text-neutral-800 disabled:opacity-60"
          >
            {replicandoHistorico ? "Replicando..." : "Replicar base"}
          </button>
          {loadingPreviewHistorico && (
            <p className="text-xs text-neutral-500">Carregando pré-visualização…</p>
          )}
        </div>
      )}

      {semanaCorrente && !baseCompleta && !isAdmin && colunaEditavelHoje && (
        <div className="mb-4 rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          Hoje você pode editar a coluna{" "}
          <strong>{colunaEditavelHoje.label}</strong>. As alterações são salvas
          automaticamente a cada 20 segundos ou ao clicar em Salvar base.
        </div>
      )}

      {!semanaCorrente && payload && (
        <div className="mb-4 rounded-md border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-700">
          <p className="font-semibold">Consulta de semana anterior</p>
          <p className="mt-1">
            Visualizando a semana de {formattedSegunda}. Cadastro só na semana
            corrente (
            {BrazilDate.formatBR(BrazilDate.fromISOString(segundaAtual), false)}
            ).
          </p>
        </div>
      )}

      {baseCompleta && payload && (
        <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <p className="font-semibold">Semana com todas as colunas completas</p>
          <p className="mt-1">
            Todos os dias de funcionamento cadastrados e salvos. A produção de
            cada dia usa a base da coluna correspondente.
          </p>
        </div>
      )}

      {semanaCorrente && !baseCompleta && payload && isAdmin && (
        <div className="mb-4 rounded-md border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-700">
          Alterações são salvas automaticamente a cada 20 segundos na coluna
          editada. Colunas com fundo verde claro indicam dia completo. Use{" "}
          <strong>Salvar base</strong> para gravar manualmente.
        </div>
      )}

      {usarPreviewHistorico && historicoOrigemLabel && (
        <div className="mb-4 rounded-md border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900">
          Visualizando sugestões de <strong>{historicoOrigemLabel}</strong>.
          Células em branco mostram as quantidades dessa semana; as unidades de
          medida refletem o mesmo período. Use <strong>Replicar base</strong>{" "}
          para aplicar na tela ou gravar.
        </div>
      )}

      {!usarPreviewHistorico && temSugestaoAnterior && podeEditarAlgumaColuna && (
        <div className="mb-4 rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          Células em branco foram sugeridas com a base do mesmo dia da semana
          passada.
        </div>
      )}

      {autoSaving && (
        <p className="mb-2 text-xs text-neutral-500">Salvando alterações…</p>
      )}

      <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white transition-shadow duration-200 hover:shadow-[0_12px_20px_-8px_rgba(15,23,42,0.16)]">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50">
            <tr>
              <th className="sticky left-0 z-10 min-w-[10rem] bg-neutral-50 px-3 py-3 text-left font-semibold text-neutral-600">
                Descrição
              </th>
              <th className="min-w-[5rem] px-2 py-3 text-left font-semibold text-neutral-600">
                Peso
              </th>
              {diasColunas.map((col) => (
                <th
                  key={col.diaSemana}
                  className={`min-w-[4.5rem] px-1 py-3 text-center font-semibold ${
                    col.ehHoje
                      ? "bg-amber-50 text-amber-950"
                      : "text-neutral-600"
                  }`}
                  title={col.labelCompleto}
                >
                  <span className="block">{col.label}</span>
                  {col.ehHoje && (
                    <span className="mt-0.5 block text-[10px] font-normal text-amber-800">
                      hoje
                    </span>
                  )}
                </th>
              ))}
              <th className="min-w-[5.5rem] px-2 py-3 text-left font-semibold text-neutral-600">
                UND
              </th>
            </tr>
          </thead>
          <tbody>
            {gruposPorSetor.map((grupo) => (
              <React.Fragment key={grupo.key}>
                {grupo.nome ? (
                  <tr className="border-t-2 border-neutral-300">
                    <td className="sticky left-0 z-10 bg-neutral-100 px-3 py-2 text-xs font-bold uppercase tracking-wide text-neutral-700">
                      {grupo.nome}
                      <span className="ml-2 font-medium normal-case text-neutral-500">
                        {grupo.itens.length}{" "}
                        {grupo.itens.length === 1 ? "item" : "itens"}
                      </span>
                    </td>
                    <td
                      colSpan={colunasTabela - 1}
                      className="bg-neutral-100"
                    />
                  </tr>
                ) : null}
                {grupo.itens.map((item) => {
                  const setoresExtras = nomesSetoresExtras(
                    item,
                    grupo.setorId
                  );
                  return (
                    <tr
                      key={item.itemId}
                      className="group border-t border-neutral-200 transition-colors duration-200 hover:bg-neutral-50/90"
                    >
                      <td className="sticky left-0 z-10 bg-white px-3 py-2 font-medium text-neutral-800 transition-colors duration-200 group-hover:bg-neutral-50">
                        {item.descricao}
                        {setoresExtras ? (
                          <p className="mt-0.5 text-[11px] font-normal text-neutral-500">
                            Também em {setoresExtras}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-2 py-2 text-neutral-600">
                        {item.pesoPadrao}
                      </td>
                      {diasColunas.map((col) => {
                        const cel = getCelulaDia(item, col.diaSemana);
                        const podeEditar = isAdmin && col.permiteEdicao;
                        const placeholder = sugestaoExibicao(
                          item.itemId,
                          col.diaSemana,
                          cel,
                          previewHistoricoLinhas,
                          usarPreviewHistorico
                        );
                        const tituloSugestao = usarPreviewHistorico
                          ? `Sugestão de ${historicoOrigemLabel}`
                          : cel?.preenchidoDaSemanaAnterior
                            ? "Sugestão da semana passada"
                            : col.labelCompleto;
                        return (
                          <td
                            key={col.diaSemana}
                            className={`px-1 py-2 ${
                              col.ehHoje ? "bg-amber-50/60" : ""
                            } ${podeEditar && !col.ehHoje ? "bg-sky-50/40" : ""}`}
                          >
                            <input
                              value={valorLinha(
                                linhas,
                                item.itemId,
                                col.diaSemana
                              )}
                              onChange={(e) =>
                                atualizarCelula(
                                  item.itemId,
                                  col.diaSemana,
                                  e.target.value
                                )
                              }
                              disabled={!podeEditar || saving}
                              placeholder={placeholder}
                              title={tituloSugestao}
                              className={`w-full min-w-[3.5rem] rounded border border-neutral-300 px-1.5 py-1.5 text-center text-sm shadow-[inset_0_1px_2px_rgba(15,23,42,0.06)] disabled:bg-neutral-100 ${
                                podeEditar && !saving ? hover3dCampoClass : ""
                              }`}
                            />
                          </td>
                        );
                      })}
                      <td className="px-2 py-2">
                        <UndMedidaSelect
                          unds={unds}
                          value={
                            usarPreviewHistorico &&
                            !undSobrescritosNaPreview.has(item.itemId)
                              ? (previewHistoricoUnd[item.itemId] ??
                                linhasUnd[item.itemId] ??
                                "")
                              : (linhasUnd[item.itemId] ?? "")
                          }
                          onChange={(undMedidaId) =>
                            atualizarUnd(item.itemId, undMedidaId)
                          }
                          disabled={!podeEditarAlgumaColuna || saving}
                          className={`w-full min-w-[4.5rem] ${
                            podeEditarAlgumaColuna && !saving
                              ? hover3dCampoClass
                              : ""
                          }`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
            {!loading && itensMatriz.length === 0 && (
              <tr>
                <td
                  className="px-4 py-6 text-center text-neutral-500"
                  colSpan={colunasTabela}
                >
                  Nenhum item ativo para a loja.{" "}
                  <Link
                    to="/controle-producao/itens"
                    className="text-primary underline"
                  >
                    Cadastrar itens
                  </Link>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {payload && (
        <OrientacoesProducaoForm
          lojaIdReferencia={resolveLojaId()}
          orientacao={payload.orientacao}
          podeEditar={podeEditarOrientacao}
          onSaved={(orientacao: OrientacaoProducaoData) => {
            setPayload((prev) => (prev ? { ...prev, orientacao } : prev));
          }}
        />
      )}

      {payload && isAdmin && podeEditarAlgumaColuna && (
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={() => void saveTudo()}
            disabled={saving}
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {saving ? "Salvando..." : "Salvar base"}
          </button>
        </div>
      )}

      {loading && (
        <p className="mt-3 text-sm text-neutral-500">
          Carregando base semanal...
        </p>
      )}

      <Modal
        isOpen={modalReplicarHistorico}
        onClose={() =>
          !replicandoHistorico && setModalReplicarHistorico(false)
        }
        title="Replicar base anterior"
        panelClassName="max-w-lg"
      >
        <p className="text-sm text-neutral-700">
          Como deseja usar os dados de{" "}
          <strong>{historicoOrigemLabel || "semana selecionada"}</strong> em
          todas as colunas da semana atual?
        </p>
        <ul className="mt-3 list-inside list-disc space-y-1 text-sm text-neutral-600">
          <li>
            <strong>Apenas replicar</strong> preenche a tela para revisão, sem
            gravar.
          </li>
          <li>
            <strong>Replicar e salvar</strong> grava imediatamente no banco.
          </li>
        </ul>
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            disabled={replicandoHistorico}
            onClick={() => setModalReplicarHistorico(false)}
            className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-700 disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={replicandoHistorico || loadingPreviewHistorico}
            onClick={apenasReplicarHistorico}
            className="rounded-md border border-neutral-300 bg-white px-4 py-2 text-sm font-semibold text-neutral-800 disabled:opacity-60"
          >
            Apenas replicar
          </button>
          <button
            type="button"
            disabled={replicandoHistorico || loadingPreviewHistorico}
            onClick={() => void replicarESalvarHistorico()}
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {replicandoHistorico ? "Salvando..." : "Replicar e salvar"}
          </button>
        </div>
      </Modal>
    </main>
  );
};

export default ControleProducaoBaseSemanalPage;
